<script setup lang="ts">
import type { Column, SortDirection, SortingColumnDef } from '@tanstack/vue-table'
import type { HTMLAttributes } from 'vue'
import {
  ArrowLongDownIcon,
  ArrowLongUpIcon,
  ChevronUpDownIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/vue/24/outline'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '#ui-kit/ui/tooltip'

type SortableColumn = Pick<Column<unknown, unknown>, 'getCanSort' | 'getIsSorted' | 'getToggleSortingHandler'> & {
  columnDef: Pick<SortingColumnDef<unknown>, 'enableSorting'>
}

const props = defineProps<{
  column: SortableColumn
  label: string
  tooltip?: string
  class?: HTMLAttributes['class']
}>()

const canSort = computed(() => props.column.columnDef.enableSorting === true && props.column.getCanSort())

function sortIcon(direction: false | SortDirection) {
  switch (direction) {
    case 'asc':
      return ArrowLongUpIcon
    case 'desc':
      return ArrowLongDownIcon
    default:
      return ChevronUpDownIcon
  }
}
</script>

<template>
  <div :class="cn('inline-flex items-center gap-1 px-2', props.class)">
    <span
      class="min-w-0"
      :class="{ 'cursor-pointer select-none': canSort }"
      @click="canSort && column.getToggleSortingHandler()?.($event)"
    >
      {{ label }}
    </span>

    <Tooltip v-if="tooltip">
      <TooltipTrigger as-child>
        <button type="button" class="inline-flex" aria-label="More information">
          <QuestionMarkCircleIcon class="size-4 shrink-0 text-brand-300" />
        </button>
      </TooltipTrigger>
      <TooltipContent size="lg">
        {{ tooltip }}
      </TooltipContent>
    </Tooltip>

    <button
      v-if="canSort"
      type="button"
      class="inline-flex cursor-pointer select-none"
      :aria-label="`Sort by ${label}`"
      @click="column.getToggleSortingHandler()?.($event)"
    >
      <component :is="sortIcon(column.getIsSorted())" class="size-3.5 shrink-0 text-secondary" />
    </button>
  </div>
</template>
